import { ModalStackIdleContext, TryShowModalContext } from 'contexts/ModalProvider'
import { useAppSelector } from 'hooks/reduxHooks'
import { useLabRouteRequestContext } from 'hooks/useLabRouteRequestContext'
import { popupForPage, popupsFromUis } from 'lib/labV2/popups'
import { SNACKBAR_AFTER_POPUP_MS, snackbarForPage, snackbarsFromUis } from 'lib/labV2/snackbars'
import { useContext, useEffect, useMemo, useRef } from 'react'
import { isLabV2ModalSuppressed, recordLabV2ModalSeen } from 'storage/labV2Modal'
import { isLabV2SnackbarSuppressed } from 'storage/labV2Snackbar'
import LabV2PopupModal from './LabV2PopupModal'
import { presentLabV2Snackbar } from '../LabSnackbar/useLabV2Snackbar'

export default function useLabV2Modal({ enabled }: { enabled: boolean }) {
  const tryShowModal = useContext(TryShowModalContext)
  const isModalStackIdle = useContext(ModalStackIdleContext)
  const { pathname, currentRegionCode } = useLabRouteRequestContext()
  const uis = useAppSelector(state => state.system.features.labV2?.uis)
  const popups = useMemo(() => popupsFromUis(uis), [uis])
  const snackbarGenerationRef = useRef(0)
  const snackbarTimeoutRef = useRef(0)

  // Modal-stack changes must not cancel delivery; leaving the page must.
  useEffect(() => () => {
    snackbarGenerationRef.current += 1
    window.clearTimeout(snackbarTimeoutRef.current)
  }, [pathname, currentRegionCode, enabled])

  const popup = useMemo(() => {
    if (!enabled || !isModalStackIdle) {
      return undefined
    }
    const candidate = popupForPage(popups, pathname, currentRegionCode)
    if (!candidate || isLabV2ModalSuppressed(candidate)) {
      return undefined
    }
    return candidate
  }, [enabled, isModalStackIdle, popups, pathname, currentRegionCode])

  useEffect(() => {
    if (!isModalStackIdle || !popup || isLabV2ModalSuppressed(popup)) {
      return
    }

    const snackbarGeneration = snackbarGenerationRef.current
    const snackbar = snackbarForPage(snackbarsFromUis(uis), pathname, currentRegionCode)
    const closing = tryShowModal(() => {
      recordLabV2ModalSeen(popup)
      return (
        <LabV2PopupModal popup={popup} />
      )
    })
    closing?.then(() => {
      if (snackbarGeneration !== snackbarGenerationRef.current || !snackbar || isLabV2SnackbarSuppressed(snackbar)) {
        return
      }
      snackbarTimeoutRef.current = window.setTimeout(() => {
        presentLabV2Snackbar(snackbar)
      }, SNACKBAR_AFTER_POPUP_MS)
    })
  }, [currentRegionCode, isModalStackIdle, pathname, popup, tryShowModal, uis])
}
